export const REQUEST_TYPE_KEYS = ['supplies', 'change_ownership', 'invoice', 'account_change', 'other_type'];

// Tipologías que exigen dirección, código postal y municipio del suministro
// (igual que `SupplyAddressRules` en el back).
export const ADDRESS_REQUIRED_TYPES = ['supplies', 'change_ownership'];

export const ADDRESS_FIELDS = ['supply_address', 'postal_code', 'municipality'];

const POSTAL_CODE_PATTERN = /^[0-9]{5}$/;

export const normalizeOtherActionOptions = (value) => {
  if (!Array.isArray(value)) return [];

  const seen = new Set();
  return value.filter((option) => {
    const key = typeof option?.key === 'string' ? option.key.trim() : '';
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Al pasar a una tipología que exige dirección, el back la valida con los
// datos guardados; muchas solicitudes migradas no la tienen, así que el
// cambio debe completarla.
export const requiresSupplyAddress = (type, currentType) => (
  ADDRESS_REQUIRED_TYPES.includes(type) && type !== currentType
);

const filled = (value) => (typeof value === 'string' ? value.trim() : '');

// Lo guardado en la solicitud manda; si falta, se toma del suministro y del
// inmueble vinculados. Nunca de la dirección de correspondencia, que en el
// legacy a veces trae un correo.
export const requestAddressDefaults = (request) => {
  const supplies = Array.isArray(request?.supplies) ? request.supplies : [];
  const fromSupply = (field) => supplies.map((supply) => filled(supply?.[field])).find(Boolean) || '';
  const supplyAddress = filled(request?.supply_address) || fromSupply('address') || filled(request?.property?.address);

  return {
    supply_address: supplyAddress,
    postal_code: filled(request?.postal_code) || fromSupply('postal_code') || filled(request?.property?.postal_code),
    municipality: filled(request?.municipality) || fromSupply('municipality') || filled(request?.property?.municipality),
  };
};

export const isValidPostalCode = (value) => POSTAL_CODE_PATTERN.test(filled(value));

export const isSupplyAddressComplete = (address) => Boolean(
  filled(address?.supply_address) && isValidPostalCode(address?.postal_code) && filled(address?.municipality)
);

// Campos que faltan para poder guardar, en el orden del formulario, para
// explicar por qué el botón está deshabilitado.
export const missingRequestTypeFields = (type, otherAction, address, needsAddress) => {
  const missing = [];
  if (type === 'other_type' && !otherAction) missing.push('other_action');
  if (!needsAddress) return missing;
  if (!filled(address?.supply_address)) missing.push('supply_address');
  if (!isValidPostalCode(address?.postal_code)) missing.push('postal_code');
  if (!filled(address?.municipality)) missing.push('municipality');
  return missing;
};

// Motivos de rechazo del back (`RequestController::update`) traducidos a
// claves de `requestTypeChange.errors`. El back responde en inglés: un 422
// con los campos que fallan o un 403 con el motivo en `value`.
const FIELD_ERROR_KEYS = {
  type: 'type',
  other_action: 'otherAction',
  supply_address: 'supplyAddress',
  postal_code: 'postalCode',
  municipality: 'municipality',
};

const FORBIDDEN_ERROR_KEYS = {
  'Only ADS can change the request typology': 'onlyAds',
  'This request is assigned to another manager': 'assignedToAnother',
  'You do not have permission to access this request': 'noPermission',
};

export const requestTypeErrorKeys = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 422 && data?.errors && typeof data.errors === 'object') {
    const keys = Object.keys(data.errors)
      .map((field) => FIELD_ERROR_KEYS[field])
      .filter(Boolean)
      .map((key) => `requestTypeChange.errors.${key}`);
    if (keys.length) return [...new Set(keys)];
  }
  if (status === 403) {
    return [`requestTypeChange.errors.${FORBIDDEN_ERROR_KEYS[data?.value] ?? 'forbidden'}`];
  }
  if (status === 401) return ['requestTypeChange.errors.session'];

  return ['requestTypeChange.errors.generic'];
};

export const canSaveRequestType = (type, otherAction, loading = false, hasError = false) => Boolean(
  type && !loading && !hasError && (type !== 'other_type' || otherAction)
);

export const requestTypePayload = (type, otherAction, address = null) => {
  const payload = {
    type,
    other_action: type === 'other_type' ? otherAction : null,
  };

  if (address) {
    ADDRESS_FIELDS.forEach((field) => {
      payload[field] = filled(address[field]);
    });
  }

  return payload;
};
